import { useCallback, useEffect, useState } from 'react';
import { currentLanguage } from '../i18n/strings.js';

// ============================================================================
// PREÇOS DA PÁGINA DE OFERTA. Fonte de verdade = Stripe, via a função
// get-prices.js (somente leitura). Nada de moeda convertida no frontend.
//
// Mapa idioma → moeda (stripe-setup-progresso.md): pt→BRL | en→USD |
// es/fr/it/de→EUR | no→NOK | hi→INR.
// ============================================================================

const CURRENCY_BY_LANG = { pt: 'BRL', en: 'USD', es: 'EUR', fr: 'EUR', it: 'EUR', de: 'EUR', no: 'NOK', hi: 'INR' };
const LOCALE_BY_LANG = { pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR', it: 'it-IT', de: 'de-DE', no: 'nb-NO', hi: 'hi-IN' };

export const currencyForLanguage = (lang = currentLanguage) => CURRENCY_BY_LANG[lang] ?? 'USD';

export function formatMoney(minorUnits, currency, lang = currentLanguage) {
  return new Intl.NumberFormat(LOCALE_BY_LANG[lang] ?? 'en-US', { style: 'currency', currency }).format(minorUnits / 100);
}

const PLAN_ID_BY_INTERVAL = { week: 'fitclutch_weekly', month: 'fitclutch_monthly', year: 'fitclutch_annual' };

// Valores REAIS cadastrados no Stripe (stripe-setup-progresso.md, 19–20/09/2026),
// usados SÓ como reserva quando a função get-prices não responde — por
// exemplo no `npm run dev` local, que não roda as Netlify Functions. Em
// produção a tela sempre usa o que o Stripe devolve.
const FALLBACK = {
  // BRL mensal/anual (4990 / 24990) = valores APROVADOS em 05/10/2026 mas que
  // ainda precisam ser cadastrados no Stripe (preço em BRL nos produtos
  // Monthly e Annual). Em produção a página usa o que o Stripe devolve.
  BRL: [{ interval: 'week', amount: 1990 }, { interval: 'month', amount: 4990 }, { interval: 'year', amount: 24990 }],
  USD: [{ interval: 'month', amount: 2999 }, { interval: 'year', amount: 6999 }],
  EUR: [{ interval: 'month', amount: 2999 }, { interval: 'year', amount: 6999 }],
  NOK: [{ interval: 'month', amount: 29900 }, { interval: 'year', amount: 69900 }],
  INR: [{ interval: 'month', amount: 119900 }, { interval: 'year', amount: 279900 }],
};
const FALLBACK_COUPON_PCT = 50;

const DAYS = { week: 7, month: 365 / 12, year: 365 };

// Normaliza → o formato único que os componentes da oferta consomem.
function buildPlans(rawPlans, couponPct, currency) {
  const many = rawPlans.length > 1;
  return rawPlans.map((p) => {
    const promoAmount = couponPct ? Math.round(p.amount * (1 - couponPct / 100)) : null;
    const shown = promoAmount ?? p.amount;
    return {
      id: p.interval,
      planId: p.planId ?? PLAN_ID_BY_INTERVAL[p.interval],
      interval: p.interval,
      currency,
      amount: p.amount, // preço cheio (renovação)
      promoAmount, // preço da 1ª cobrança (null = sem desconto)
      discountPct: promoAmount ? couponPct : null,
      perDayAmount: Math.round(shown / DAYS[p.interval]),
      perDayApprox: p.interval === 'month', // mês não tem nº fixo de dias
      badge: !many ? null : p.interval === 'month' ? 'popular' : p.interval === 'year' ? 'best' : null,
      highlight: !many || p.interval === 'month',
    };
  });
}

export function usePlans() {
  const [state, setState] = useState({ status: 'loading', plans: [], currency: currencyForLanguage(), source: null });
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const currency = currencyForLanguage();
    setState((s) => ({ ...s, status: 'loading' }));

    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 7000);
    fetch(`/.netlify/functions/get-prices?lang=${currentLanguage}`, { signal: ctl.signal })
      .then(async (res) => {
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) throw new Error('no_function'); // ex: vite dev devolve index.html
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'stripe_error');
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        const pct = data.coupon?.percentOff ?? null;
        setState({ status: 'ready', plans: buildPlans(data.plans, pct, data.currency?.toUpperCase() ?? currency), currency, source: 'stripe' });
      })
      .catch(() => {
        if (cancelled) return;
        // Sem Netlify Functions (dev local) → reserva com os valores reais documentados.
        const fb = FALLBACK[currency] ?? FALLBACK.USD;
        setState({ status: 'ready', plans: buildPlans(fb, FALLBACK_COUPON_PCT, currency), currency, source: 'fallback' });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      ctl.abort();
    };
  }, [nonce]);

  return { ...state, retry };
}
